package dev.lingvo.site.pages.app.projects.slug.settings

import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.attr
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.minHeight
import com.varabyte.kobweb.compose.ui.modifiers.minWidth
import com.varabyte.kobweb.compose.ui.modifiers.outline
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.selectors.focusVisible
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.lingvo.shared.ProjectAccentKey
import dev.lingvo.shared.ProjectIconKey
import dev.lingvo.shared.ProjectKind
import dev.lingvo.site.ds.IconSize
import dev.lingvo.site.ds.LCheckIcon
import dev.lingvo.site.ds.LProjectMark
import dev.lingvo.site.ds.Motion
import dev.lingvo.site.ds.Radius
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.TypeScale
import dev.lingvo.site.ds.iconSize
import dev.lingvo.site.ds.toSitePalette
import dev.lingvo.site.ds.transitionColors
import dev.lingvo.site.ds.transitionSurface
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tr
import kotlin.random.Random
import kotlinx.browser.document
import org.jetbrains.compose.web.attributes.ButtonType
import org.jetbrains.compose.web.attributes.type
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLElement

val ProjectIdentityChoiceStyle = CssStyle {
    base {
        Modifier
            .minWidth(0.px)
            .minHeight(64.px)
            .backgroundColor(colorMode.toSitePalette().surface)
            .color(colorMode.toSitePalette().textPrimary)
            .border(1.px, LineStyle.Solid, colorMode.toSitePalette().borderStrong)
            .borderRadius(Radius.Control)
            .padding(Space.SM)
            .cursor(Cursor.Pointer)
            .transitionColors()
            .styleModifier {
                property("display", "flex")
                property("align-items", "center")
                property("justify-content", "flex-start")
                property("gap", Space.SM.toString())
                // `anywhere`, not `break-word`, split three of the eight labels mid-word on the
                // desktop grid — "Monogr/am", "Smartp/hone", "Gamepa/d" — because it both permits a
                // break inside a word *and* drops the tile's min-content width to one glyph, which
                // let the auto-fit track minimum below pack a fifth column into the form measure.
                // A label is a word: it breaks at a space or not at all.
                property("overflow-wrap", "break-word")
            }
    }
    focusVisible {
        Modifier
            .outline(2.px, LineStyle.Solid, colorMode.toSitePalette().accent)
            .styleModifier { property("outline-offset", "2px") }
    }
    cssRule(":not([aria-checked=\"true\"]):hover") {
        Modifier.backgroundColor(colorMode.toSitePalette().surfaceSunken)
    }
}

@Composable
internal fun ProjectIdentityPicker(
    name: String,
    iconKey: ProjectIconKey,
    accentKey: ProjectAccentKey,
    onIconSelected: (ProjectIconKey) -> Unit,
    onAccentSelected: (ProjectAccentKey) -> Unit,
) {
    ChoiceGroup(
        label = t("app.projects.slug.settings.project_identity_picker.project_icon", "Project icon"),
        dataAttribute = "data-project-icon-picker",
        values = ProjectIconKey.entries,
        selected = iconKey,
        valueName = ProjectIconKey::name,
        visibleLabel = ProjectIconKey::displayLabel,
        onSelected = onIconSelected,
    ) { icon -> LProjectMark(name, icon, accentKey) }
    ChoiceGroup(
        label = t("app.projects.slug.settings.project_identity_picker.accent", "Accent"),
        dataAttribute = "data-project-accent-picker",
        values = ProjectAccentKey.entries,
        selected = accentKey,
        valueName = ProjectAccentKey::name,
        visibleLabel = ProjectAccentKey::displayLabel,
        onSelected = onAccentSelected,
        optionAttribute = "data-accent-key",
    ) { accent -> LProjectMark(name, iconKey, accent) }
}

@Composable
private fun <T> ChoiceGroup(
    label: String,
    dataAttribute: String,
    values: List<T>,
    selected: T,
    valueName: (T) -> String,
    visibleLabel: (T) -> String,
    onSelected: (T) -> Unit,
    optionAttribute: String = "data-icon-key",
    preview: @Composable (T) -> Unit,
) {
    val baseId = remember { "project-choice-${Random.nextInt(1_000_000)}" }
    Column(Modifier.fillMaxWidth().gap(Space.SM)) {
        Span(Modifier.fontSize(TypeScale.Small).fontWeight(600).toAttrs()) { Text(label) }
        Div(
            Modifier
                .fillMaxWidth()
                .gap(Space.SM)
                .styleModifier {
                    property("display", "grid")
                    // 168, not 132: at the 768px settings form measure 132 fits five columns of
                    // ~144px, which is narrower than the two-column phone layout the same labels
                    // sit on one line in. 168 resolves to four columns on desktop and still two at
                    // 390 (358px measure -> 175px tracks).
                    property("grid-template-columns", "repeat(auto-fit, minmax(min(100%, 168px), 1fr))")
                }
                .attr("role", "radiogroup")
                .attr("aria-label", label)
                .attr(dataAttribute, "")
                .toAttrs(),
        ) {
            values.forEachIndexed { index, value ->
                val checked = value == selected
                val name = valueName(value)
                Button(
                    ProjectIdentityChoiceStyle.toModifier()
                        .then(
                            if (checked) {
                                Modifier
                                    .backgroundColor(ColorMode.current.toSitePalette().accentSubtle)
                                    .border(1.px, LineStyle.Solid, ColorMode.current.toSitePalette().accent)
                            } else {
                                Modifier
                            },
                        )
                        .toAttrs {
                            id("$baseId-$index")
                            type(ButtonType.Button)
                            attr("role", "radio")
                            attr("aria-checked", checked.toString())
                            attr("data-value", name)
                            attr(optionAttribute, name)
                            tabIndex(if (checked) 0 else -1)
                            onClick { onSelected(value) }
                            onKeyDown { event ->
                                val target = radioTarget(event.key, index, values.size)
                                if (target != null) {
                                    event.preventDefault()
                                    onSelected(values[target])
                                    (document.getElementById("$baseId-$target") as? HTMLElement)?.focus()
                                }
                            }
                        },
                ) {
                    preview(value)
                    Span(
                        Modifier.styleModifier {
                            property("min-width", "0")
                            property("flex", "1 1 auto")
                        }.toAttrs(),
                    ) {
                        Text(visibleLabel(value))
                    }
                    Span(
                        Modifier
                            // The slot the tick occupies whether or not it is showing, so choosing
                            // an option does not shift the row. One rung, named once: the same 20
                            // had been spelled out three times here, including inside the `flex`
                            // string where nothing would have caught it drifting from the other two.
                            .iconSize(IconSize.XL)
                            .color(ColorMode.current.toSitePalette().accent)
                            .styleModifier {
                                property("display", "inline-flex")
                                property("align-items", "center")
                                property("justify-content", "center")
                                property("flex", "0 0 ${IconSize.XL}")
                                property("opacity", if (checked) "1" else "0")
                                property("transform", if (checked) "scale(1)" else "scale(.76)")
                            }
                            .transitionSurface(durationMs = Motion.FastMs)
                            .attr("data-selection-indicator", "")
                            .attr("aria-hidden", "true")
                            .toAttrs(),
                    ) {
                        LCheckIcon(Modifier.iconSize(IconSize.LG))
                    }
                }
            }
        }
    }
}

private fun radioTarget(key: String, current: Int, size: Int): Int? = when (key) {
    "ArrowRight", "ArrowDown" -> (current + 1) % size
    "ArrowLeft", "ArrowUp" -> (current - 1 + size) % size
    "Home" -> 0
    "End" -> size - 1
    else -> null
}

/**
 * The kinds of project, as a person picks them.
 *
 * The platform names stay as they are — "Android", "iOS" and "Kotlin Multiplatform" are proper
 * nouns and a translator should leave them alone — but they still go through the catalogue, because
 * that is the difference between a translator seeing them and deciding, and never being shown them
 * at all. "Web" and "Other" are ordinary words and want translating everywhere.
 */
internal fun ProjectKind.displayLabel(): String = when (this) {
    ProjectKind.KMP -> tr("app.projects.slug.settings.project_identity_picker.kind_kmp", "Kotlin Multiplatform")
    ProjectKind.ANDROID -> tr("app.projects.slug.settings.project_identity_picker.kind_android", "Android")
    ProjectKind.IOS -> tr("app.projects.slug.settings.project_identity_picker.kind_ios", "iOS")
    ProjectKind.WEB -> tr("app.projects.slug.settings.project_identity_picker.kind_web", "Web")
    ProjectKind.OTHER -> tr("app.projects.slug.settings.project_identity_picker.kind_other", "Other")
}

/** The icon names, which are read out when somebody picks one by keyboard. */
private fun ProjectIconKey.displayLabel(): String = when (this) {
    ProjectIconKey.MONOGRAM -> tr("app.projects.slug.settings.project_identity_picker.icon_monogram", "Monogram")
    ProjectIconKey.BRACES -> tr("app.projects.slug.settings.project_identity_picker.icon_braces", "Braces")
    ProjectIconKey.GLOBE_2 -> tr("app.projects.slug.settings.project_identity_picker.icon_globe", "Globe")
    ProjectIconKey.SMARTPHONE -> tr("app.projects.slug.settings.project_identity_picker.icon_smartphone", "Smartphone")
    ProjectIconKey.APP_WINDOW -> tr("app.projects.slug.settings.project_identity_picker.icon_app_window", "App window")
    ProjectIconKey.BOX -> tr("app.projects.slug.settings.project_identity_picker.icon_box", "Box")
    ProjectIconKey.BOOK_OPEN -> tr("app.projects.slug.settings.project_identity_picker.icon_book", "Book")
    ProjectIconKey.GAMEPAD_2 -> tr("app.projects.slug.settings.project_identity_picker.icon_gamepad", "Gamepad")
}

/**
 * The accent names, in the reader's language.
 *
 * A swatch is a mark, so this text is the whole of what a screen reader announces for the option —
 * and it was `ProjectAccentKey.name` prettified, which the extractor cannot reach. The icon group
 * beside it was fully translated, so the two halves of one picker spoke different languages.
 */
private fun ProjectAccentKey.displayLabel(): String = when (this) {
    ProjectAccentKey.INK -> tr("app.projects.slug.settings.project_identity_picker.accent_ink", "Ink")
    ProjectAccentKey.COBALT -> tr("app.projects.slug.settings.project_identity_picker.accent_cobalt", "Cobalt")
    ProjectAccentKey.JADE -> tr("app.projects.slug.settings.project_identity_picker.accent_jade", "Jade")
    ProjectAccentKey.AMBER -> tr("app.projects.slug.settings.project_identity_picker.accent_amber", "Amber")
    ProjectAccentKey.CORAL -> tr("app.projects.slug.settings.project_identity_picker.accent_coral", "Coral")
    ProjectAccentKey.VIOLET -> tr("app.projects.slug.settings.project_identity_picker.accent_violet", "Violet")
}
